/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Building2 } from 'lucide-react';
import type { RepositorySettings } from '@shared/types/organizationSettings';

interface RepositoryDefaultsSectionProps {
  settings: RepositorySettings;
  onChange: (settings: RepositorySettings) => void;
  onDirty: () => void;
}

export function RepositoryDefaultsSection({ 
  settings, 
  onChange, 
  onDirty 
}: RepositoryDefaultsSectionProps) {
  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Building2 size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Repository Defaults</h3>
      </div>
      
      <div className="form-group">
        <label htmlFor="default-branch">Default Branch</label>
        <input
          id="default-branch"
          type="text"
          value={settings.defaultBranch || 'main'}
          onChange={(e) => {
            onChange({ ...settings, defaultBranch: e.target.value });
            onDirty();
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
          The main branch for your repositories (usually "main" or "master")
        </small>
      </div>
      
      <div className="form-group">
        <label htmlFor="product-data-path">Product Data Path</label>
        <input
          id="product-data-path"
          type="text"
          value={settings.productDataPath || 'data/product'}
          onChange={(e) => {
            onChange({ ...settings, productDataPath: e.target.value });
            onDirty();
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
          Directory where product specification files are stored
        </small>
      </div>
      
      <div className="form-group">
        <label htmlFor="velocity-branch">Velocity Branch Name</label>
        <input
          id="velocity-branch"
          type="text"
          value={settings.velocityBranchName || 'velocity'}
          onChange={(e) => {
            onChange({ ...settings, velocityBranchName: e.target.value });
            onDirty();
          }}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
          The working branch for Velocity changes
        </small>
      </div>
    </div>
  );
}
